'use client';

import { isPathMatch } from '@/@studio/utils';
import { Routes } from '@/app/routes';
import { useSession } from '@/providers/enhanced-session';
import { cn, Link, ScrollShadow, Spacer, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import NextImage from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useRef, useState, type Key, type ReactNode } from 'react';
import { useNavSections } from '../hooks/use-nav-sections';
import { NavDeveloperLink } from './nav-developer-link';
import { NavFooter } from './nav-footer';
import { NavHoverPeek } from './nav-hover-peek';
import { NavRailItem } from './nav-rail-item';
import { NavSectionGroup } from './nav-section';
import { SynthesizeChannelsModal } from './synthesize-channels-modal';

interface NavShellProps {
    isCollapsed: boolean;
    /** Expands the sidebar (or opens the mobile drawer) — same handler the
     * collapse toggle button uses. */
    onToggle: () => void;
    onProfileMenuAction: (key: Key) => void;
    viewAsControl?: ReactNode;
}

/**
 * The left-hand nav (JTBD redesign, permanent since M4S-5697). Purely presentational — all
 * shared state (collapse, mobile drawer, help modal, profile menu actions,
 * View-as gating) still lives in `withSidebar.tsx` and is passed down here,
 * so none of that plumbing is duplicated.
 */
export function NavShell({
    isCollapsed,
    onToggle,
    onProfileMenuAction,
    viewAsControl,
}: NavShellProps) {
    const { data: session } = useSession();
    const pathname = usePathname();
    const router = useRouter();
    const sections = useNavSections();
    const isHomeActive = isPathMatch(Routes.HOME, pathname);

    const [hoverInfo, setHoverInfo] = useState<{
        key: string;
        top: number;
        left: number;
    } | null>(null);
    const [isSynthesizeOpen, setIsSynthesizeOpen] = useState(false);
    const railRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelHide = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    const scheduleHide = () => {
        cancelHide();
        hideTimeoutRef.current = setTimeout(() => setHoverInfo(null), 150);
    };

    const closeNow = () => {
        cancelHide();
        setHoverInfo(null);
    };

    // `NavHoverPeek` is rendered outside `ScrollShadow` (fixed-positioned,
    // computed from the hovered rail item's own rect) because ScrollShadow's
    // `overflow-y-auto` would otherwise clip anything that pops out
    // sideways past its bounds, even though it's absolutely positioned.
    const openPeek = (key: string) => {
        cancelHide();
        const el = railRefs.current[key];
        if (el) {
            const rect = el.getBoundingClientRect();
            setHoverInfo({ key, top: rect.top, left: rect.right + 8 });
        }
    };

    const hoveredSection = hoverInfo ? sections.find((s) => s.key === hoverInfo.key) : undefined;

    return (
        <div
            className={cn(
                'relative z-50 m-3 flex h-[calc(100%-24px)] w-64 flex-col transition-width',
                { 'w-[72px] items-center': isCollapsed },
            )}
        >
            {hoverInfo && isCollapsed && (
                <div
                    className="fixed inset-0 z-40 bg-[#192138]/[0.55] transition-opacity"
                    onMouseEnter={scheduleHide}
                    onClick={closeNow}
                />
            )}
            {hoverInfo && hoveredSection && isCollapsed && (
                <div
                    style={{ position: 'fixed', top: hoverInfo.top, left: hoverInfo.left }}
                    className="z-[60]"
                >
                    <NavHoverPeek
                        section={hoveredSection}
                        onClose={closeNow}
                        onMouseEnter={cancelHide}
                        onMouseLeave={scheduleHide}
                        onOpenSynthesize={() => setIsSynthesizeOpen(true)}
                    />
                </div>
            )}
            <SynthesizeChannelsModal
                isOpen={isSynthesizeOpen}
                onClose={() => setIsSynthesizeOpen(false)}
            />
            {/* White oval sits above the curtain so the dimmer can run
                under it to the left edge without recoloring the menu. */}
            <div
                className={cn(
                    'relative z-50 flex h-full w-full flex-col rounded-2xl border border-divider bg-background px-2 pb-6 pt-3 shadow-lg',
                    { 'items-center px-[3px] pb-6 pt-3': isCollapsed },
                )}
            >
                <div
                    className={cn(
                        'flex w-full items-center',
                        isCollapsed ? 'justify-center' : 'justify-end px-2',
                    )}
                >
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-divider text-default-500 hover:bg-[#F3F8FF]"
                    >
                        <Icon
                            icon={isCollapsed ? 'lucide:chevrons-right' : 'lucide:chevrons-left'}
                            width={16}
                            height={16}
                        />
                    </button>
                </div>

                <Spacer y={2} />

                {/* `overflow-x-hidden`: this is the vertical scroller
                    (overflow-y-auto), which otherwise computes overflow-x to
                    `auto` too — any child bleeding past its right padding (an
                    expanded row's flyout bridge strip, nav-section.tsx) would
                    add horizontal scrollable overflow: a scrollbar on Windows,
                    a sideways trackpad jiggle on Mac. Clip it instead. */}
                <ScrollShadow
                    className={cn('min-h-0 w-full flex-1 overflow-x-hidden', {
                        'px-1': !isCollapsed,
                    })}
                >
                    {isCollapsed ? (
                        <div className="flex flex-col gap-1">
                            <Tooltip
                                placement="right"
                                content={
                                    <div className="px-0.5 py-0.5">
                                        <p className="text-[13px] font-semibold text-white">Home</p>
                                        <p className="text-[12px] text-white/70">Your desktop.</p>
                                    </div>
                                }
                                classNames={{
                                    content: 'rounded-xl bg-[#192138] px-3 py-2 shadow-lg',
                                }}
                            >
                                <div>
                                    <NavRailItem
                                        iconSrc="/assets/images/MFourLogo.svg"
                                        label="Home"
                                        isActive={isHomeActive}
                                        onClick={() => router.push(Routes.HOME)}
                                    />
                                </div>
                            </Tooltip>
                            {sections.map((section) => (
                                <div
                                    key={section.key}
                                    ref={(el) => {
                                        railRefs.current[section.key] = el;
                                    }}
                                    onMouseEnter={() => openPeek(section.key)}
                                    onMouseLeave={scheduleHide}
                                >
                                    <NavRailItem
                                        icon={section.icon}
                                        label={section.label.split(' ')[0]}
                                        color={section.color}
                                        isActive={section.isSelected}
                                        onClick={onToggle}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            <Link
                                href={Routes.HOME}
                                className={cn(
                                    'flex items-center gap-2 rounded-large px-2 py-1 hover:!opacity-100',
                                    {
                                        'bg-[#F3F8FF] font-semibold text-foreground': isHomeActive,
                                        'text-default-600 hover:bg-[#F3F8FF]': !isHomeActive,
                                    },
                                )}
                            >
                                <span
                                    className="flex h-6 w-6 items-center justify-center rounded-lg"
                                    style={{ background: '#E0E6EF' }}
                                >
                                    <NextImage
                                        src="/assets/images/MFourLogo.svg"
                                        alt=""
                                        width={11}
                                        height={12}
                                        className="brightness-[0.7] grayscale"
                                    />
                                </span>
                                <span className="text-[13px]">Home</span>
                            </Link>
                            <hr className="mx-2 mb-1 border-t border-divider" />
                            {sections.map((section, index) => (
                                <Fragment key={section.key}>
                                    <NavSectionGroup
                                        section={section}
                                        onOpenSynthesize={() => setIsSynthesizeOpen(true)}
                                    />
                                    {index < sections.length - 1 && (
                                        <hr className="mx-2 mb-1 border-t border-divider" />
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    )}
                </ScrollShadow>

                {viewAsControl}

                <NavDeveloperLink isCollapsed={isCollapsed} />

                <Spacer y={2} />

                <NavFooter
                    isCollapsed={isCollapsed}
                    session={session}
                    onProfileMenuAction={onProfileMenuAction}
                />
            </div>
        </div>
    );
}

NavShell.displayName = 'NavShell';
